import { Alert, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Assessment, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getAmountToPayColumns, WEEKLY_WORKER_PAYMENT_ROUTES } from "src/pages/transaction/constants";

import { VIEW_WEEKLY_WORKER_PAYMENT_REPORT, VIEW_WORKER_WELFARE } from "../../constants";
import { HumanResourceService, ProjectService } from "../../services";
import { useHasPermission } from "../../utils";
import { ActionButton, CardLayout, CrudTable, FormDialog, InputField, SelectField } from "../v3";
import { PaymentIntervalEnum, WorkPaymentSourceEnum } from "./workPayment.types";

const computePayPeriod = () => {
  const today = moment();
  const startDate = today.clone().startOf("week").subtract(1, "days");
  const endDate = today.clone().endOf("week").subtract(1, "days");
  return {
    start: startDate,
    end: endDate,
    startStr: startDate.format("YYYY-MM-DD"),
    endStr: endDate.format("YYYY-MM-DD"),
    display: `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`,
  };
};

const buildPayPeriod = (startStr: string, endStr: string) => {
  const startDate = moment(startStr);
  const endDate = moment(endStr);
  return {
    start: startDate,
    end: endDate,
    startStr: startDate.format("YYYY-MM-DD"),
    endStr: endDate.format("YYYY-MM-DD"),
    display: `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`,
  };
};

// due = what's actually paid out this cycle. The opening balance is still excluded (AJCWLT-208) —
// this is this period's earnings only, not a running tab. Deduction now also reduces it (as well
// as the closing balance, see computeClosingBalance below) — a negative deduction credits it back.
const computeDue = (row: any) =>
  Number(row.totalWorked || 0) +
  Number(row.overtime || 0) -
  Number(row.welfare || 0) -
  Number(row.advancePaid || 0) -
  Number(row.deduction || 0);

// Closing balance is purely the running balance plus this cycle's deduction — deduction adds back
// onto the opening balance; nothing else here affects it. Deduction may be negative (a credit back
// to the worker), unlike welfare.
const computeClosingBalance = (row: any) => Number(row.balance || 0) + Number(row.deduction || 0);

export const AddBulkWorkerPaymentForm = ({ sessionId }: { sessionId?: string }) => {
  const isEditMode = Boolean(sessionId);

  const [payPeriod, setPayPeriod] = useState(computePayPeriod);

  const [date, setDate] = useState(() => moment().format("YYYY-MM-DD"));
  // Project is per worker-row (AJCWLT-208), not session-wide — each project's ProjectStage join
  // rows are cached here (keyed by projectId) so a row's Stage dropdown can scope to its project.
  const [projectStagesCache, setProjectStagesCache] = useState<Record<number, any[]>>({});
  const [workerRows, setWorkerRows] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [fetchingWorkData, setFetchingWorkData] = useState(false);
  const [description, setDescription] = useState("");
  const [sessionLoaded, setSessionLoaded] = useState(false);
  // Tracks edits made since the session was loaded (or last saved) — "View Report" is disabled
  // while this is true, since the report reads the saved session, not the in-memory table.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Add Worker(s) popup — lets workers be added into the table without re-fetching (and
  // overwriting) the rows already present, in both create and edit mode.
  const [addWorkerDialogOpen, setAddWorkerDialogOpen] = useState(false);
  const [pendingWorkers, setPendingWorkers] = useState<any[]>([]);

  const navigate = useNavigate();
  const canViewReport = useHasPermission(VIEW_WEEKLY_WORKER_PAYMENT_REPORT);
  const canViewWelfare = useHasPermission(VIEW_WORKER_WELFARE);

  // Paying is handled entirely via the per-project Report page now — a row with `disabled: true`
  // here means either "duplicate period" (create mode) or "already paid" (edit mode, see
  // fetchSessionData); either way CrudTable's isCellEditable already locks it from editing.
  // Once every row is in that state, nothing in the session is left to change.
  const allRowsPaid = isEditMode && workerRows.length > 0 && workerRows.every((r) => r.disabled);
  // Some, but not all, rows are already paid — editing the rest is still allowed, but changes
  // here (e.g. swapping a worker's project) can make the session confusing to reconcile against
  // what's already been paid out, so warn rather than block.
  const partiallyPaid = isEditMode && !allRowsPaid && workerRows.some((r) => r.disabled);

  const goToPrevWeek = useCallback(() => {
    setPayPeriod((prev) => {
      const newEnd = prev.end.clone().subtract(7, "days");
      const newStart = newEnd.clone().subtract(6, "days");
      return {
        start: newStart,
        end: newEnd,
        startStr: newStart.format("YYYY-MM-DD"),
        endStr: newEnd.format("YYYY-MM-DD"),
        display: `${newStart.format("DD MMM YYYY")} - ${newEnd.format("DD MMM YYYY")}`,
      };
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setPayPeriod((prev) => {
      const newEnd = prev.end.clone().add(7, "days");
      const newStart = newEnd.clone().subtract(6, "days");
      return {
        start: newStart,
        end: newEnd,
        startStr: newStart.format("YYYY-MM-DD"),
        endStr: newEnd.format("YYYY-MM-DD"),
        display: `${newStart.format("DD MMM YYYY")} - ${newEnd.format("DD MMM YYYY")}`,
      };
    });
  }, []);

  // Fetches (and caches) a project's ProjectStage join rows, keyed by projectId, so each row's
  // Stage dropdown can scope its options to the row's own selected project. Projects shared across
  // rows reuse the cached fetch.
  const ensureProjectStages = useCallback(
    async (projectId: number) => {
      if (projectStagesCache[projectId]) return projectStagesCache[projectId];
      try {
        const { data } = await ProjectService.getProjectStages({ projectId });
        const rows = data?.rows || [];
        setProjectStagesCache((prev) => ({ ...prev, [projectId]: rows }));
        return rows;
      } catch (error) {
        console.error("Error fetching project stages:", error);
        return [];
      }
    },
    [projectStagesCache],
  );

  // Pure mapping from pre-fetch API response to grid rows for the given workers — no state
  // writes, so callers decide whether to replace or append to workerRows.
  const fetchWorkerRows = useCallback(async (workers: any[], period: { startStr: string; endStr: string }) => {
    if (!workers.length) return [];
    const { data } = await HumanResourceService.getWeeklyWorkPaymentPreFetch({
      workerIds: workers.map((w) => w.id).join(","),
      fromDate: period.startStr,
      toDate: period.endStr,
    });
    const itemMap: any = {};
    (data?.items || []).forEach((item: any) => {
      itemMap[item.workerId] = item;
    });

    return workers.map((worker) => {
      const item = itemMap[worker.id] || {};
      const balance = Number(item.openingBalance) || 0;
      const totalWorked = Number(item.totalWage) || 0;
      const overtime = Number(item.totalOvertime) || 0;
      const welfare = Number(item.welfareAmount) || 0;
      const advancePaid = Number(item.advancePaid) || 0;
      const deduction = 0;
      const due = totalWorked + overtime - welfare - advancePaid;
      const hasExistingPayment = Boolean(item.hasExistingPayment);
      const previousPaymentPending = Boolean(item.previousPaymentPending);
      const previousPaymentPeriod =
        item.previousPaymentPeriodStart && item.previousPaymentPeriodEnd
          ? `${moment(item.previousPaymentPeriodStart).format("DD MMM YYYY")} - ${moment(item.previousPaymentPeriodEnd).format(
              "DD MMM YYYY",
            )}`
          : "";

      return {
        id: worker.id,
        workerId: worker.id,
        name: worker.name,
        balance,
        workHours: Number(item.noOfDaysWorked) || 0,
        workCount: Number(item.workCount) || 0,
        totalWorked,
        overtime,
        welfare,
        advancePaid,
        deduction,
        amountToPay: due > 0 ? due : 0,
        hasExistingPayment,
        previousPaymentPending,
        previousPaymentPeriod,
        disabled: hasExistingPayment,
        disabledReason: hasExistingPayment ? "Payment already exists for this period" : undefined,
        // Prefilled from the worker's most recently logged work entry — just a convenience
        // default, still freely overridable via the Project column.
        projectId: item.lastProjectId || undefined,
        projectName: item.lastProjectName || undefined,
      };
    });
  }, []);

  // Adds the workers picked in the popup as new rows, appended to whatever is already in the
  // table — existing rows (and any manual edits already made to them) are never re-fetched or
  // overwritten. Works the same in create and edit mode.
  const handleAddWorkers = async () => {
    if (!pendingWorkers.length) return;
    setFetchingWorkData(true);
    try {
      const newRows = await fetchWorkerRows(pendingWorkers, payPeriod);
      setWorkerRows((prev) => [...prev, ...newRows]);
      setHasUnsavedChanges(true);
      setAddWorkerDialogOpen(false);
      setPendingWorkers([]);
    } catch (error) {
      console.error("Error fetching worker payment data:", error);
      toast.error("Failed to fetch worker payment data for the period");
    } finally {
      setFetchingWorkData(false);
    }
  };

  const fetchSessionData = useCallback(
    async (id: string) => {
      setFetchingWorkData(true);
      try {
        const { data } = await HumanResourceService.getWeeklyWorkPaymentSession(id);
        const items = data?.payments || [];
        if (!items.length) {
          toast.error("Weekly payment session not found");
          navigate(-1);
          return;
        }

        const first = items[0];
        setPayPeriod(buildPayPeriod(first.startDate, first.endDate));
        setDate(moment(first.date).format("YYYY-MM-DD"));
        setDescription(first.description || "");

        // Stage loading disabled for now — the Stage column is hidden (no space for it in this
        // UI) and warming its cache on every session load is wasted work until it's re-enabled.
        // [...new Set(items.map((item: any) => item.projectId).filter(Boolean))].forEach((projectId: any) =>
        //   ensureProjectStages(projectId),
        // );

        setWorkerRows(
          items.map((item: any) => ({
            id: item.workerId,
            workerId: item.workerId,
            name: item.worker?.name || `Worker #${item.workerId}`,
            paymentId: item.id,
            transactionId: item.transactionId,
            balance: Number(item.openingBalance) || 0,
            workHours: item.noOfDays != null ? Number(item.noOfDays) : undefined,
            totalWorked: Number(item.totalWage) || 0,
            overtime: Number(item.totalOvertime) || 0,
            advancePaid: Number(item.advancePaid) || 0,
            welfare: Number(item.deduction?.welfare) || 0,
            deduction: Number(item.deduction?.otherDeduction) || 0,
            amountToPay: Number(item.paymentAmount) || 0,
            projectId: item.projectId || undefined,
            projectName: item.project?.name,
            projectStageId: item.projectStageId || undefined,
            stageId: item.projectStage?.stage?.id,
            stageName: item.projectStage?.stage?.name,
            // Paying now happens per-project from the Report page — a row already paid there is
            // locked here too (CrudTable's isCellEditable keys off `disabled`).
            disabled: Boolean(item.transactionId),
            disabledReason: item.transactionId ? "Already paid — manage via View Report" : undefined,
          })),
        );
        setSessionLoaded(true);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error fetching weekly payment session:", error);
        toast.error("Failed to load weekly payment session");
      } finally {
        setFetchingWorkData(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (isEditMode) {
      fetchSessionData(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Pay period changes invalidate any previously added rows — they're scoped to the old period.
  useEffect(() => {
    if (!isEditMode) {
      setWorkerRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriod.startStr, payPeriod.endStr]);

  // Runs synchronously inside CrudTable's processRowUpdate (before it returns the row to the
  // DataGrid) so both DataGrid's own optimistic row cache and our React state agree immediately —
  // recomputing amountToPay/closingBalance one render late (e.g. via a useMemo keyed off workerRows)
  // isn't enough since DataGrid displays processRowUpdate's return value straight away.
  // amountToPay is no longer directly editable — it's always re-derived from the row's current
  // welfare/deduction (its only editable inputs), same as closingBalance.
  const handleRowUpdate = (newRow: any, _oldRow: any) => {
    setHasUnsavedChanges(true);
    const row = { ...newRow, amountToPay: Math.max(computeDue(newRow), 0) };
    return { ...row, closingBalance: computeClosingBalance(row) };
  };

  const displayRows = useMemo(
    () => workerRows.map((row) => ({ ...row, closingBalance: computeClosingBalance(row) })),
    [workerRows],
  );

  const totalAmount = useMemo(
    () => workerRows.filter((row: any) => !row.disabled).reduce((sum: any, row: any) => sum + Number(row.amountToPay || 0), 0),
    [workerRows],
  );

  // Always the real current date/time at submit — the disabled Date field just displays today for reference.
  const buildDateTime = () => moment().toISOString();

  const resetForm = () => {
    setPayPeriod(computePayPeriod());
    setProjectStagesCache({});
    setWorkerRows([]);
    setErrors({});
    setDescription("");
    setHasUnsavedChanges(false);
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: payPeriod.startStr,
        endDate: payPeriod.endStr,
        interval: PaymentIntervalEnum.WEEKLY,
        date: buildDateTime(),
        source: WorkPaymentSourceEnum.WEEKLY_PAYMENT_UI,
        description: description || undefined,
        items: workerRows
          .filter((row) => !row.disabled)
          .map((row) => ({
            workerId: row.workerId,
            openingBalance: Number(row.balance) || 0,
            noOfDays: row.workHours != null && row.workHours !== "" ? Number(row.workHours) : undefined,
            totalWage: Number(row.totalWorked) || 0,
            totalOvertime: Number(row.overtime) || 0,
            advancePaid: Number(row.advancePaid) || 0,
            deduction: { welfare: Number(row.welfare) || 0, otherDeduction: Number(row.deduction) || 0 },
            paymentAmount: Number(row.amountToPay) || 0,
            closingBalance: computeClosingBalance(row),
            projectId: row.projectId || undefined,
            projectStageId: row.projectStageId || undefined,
          })),
      };

      await HumanResourceService.createWeeklyWorkPayment(payload);
      toast.success(`Weekly payment recorded for ${payload.items.length} worker(s).`);
      resetForm();
    } catch (error: any) {
      const response = error?.response?.data;
      const message = Array.isArray(response?.message)
        ? response.message.join(", ")
        : response?.message || "Failed to save weekly payment";
      toast.error(message);
      console.error(`🚀 ~ AddBulkWorkerPayment ~ handleCreate ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const payload = {
        date: buildDateTime(),
        description: description || undefined,
        // Already-paid rows (disabled, see fetchSessionData) are never resubmitted — the backend
        // rejects any attempt to modify them, and they have nothing left to change anyway.
        // A row with no `paymentId` was added via "Add Worker(s)" after the session was loaded —
        // it has never been saved, so `id` comes through as undefined (and is dropped by
        // JSON.stringify), telling the backend to insert a new row for `workerId` instead of
        // updating an existing one.
        items: workerRows
          .filter((row) => !row.disabled)
          .map((row) => ({
            id: row.paymentId,
            workerId: row.workerId,
            openingBalance: Number(row.balance) || 0,
            noOfDays: row.workHours != null && row.workHours !== "" ? Number(row.workHours) : undefined,
            totalWage: Number(row.totalWorked) || 0,
            totalOvertime: Number(row.overtime) || 0,
            advancePaid: Number(row.advancePaid) || 0,
            deduction: { welfare: Number(row.welfare) || 0, otherDeduction: Number(row.deduction) || 0 },
            paymentAmount: Number(row.amountToPay) || 0,
            closingBalance: computeClosingBalance(row),
            projectId: row.projectId || undefined,
            projectStageId: row.projectStageId || undefined,
          })),
      };

      await HumanResourceService.updateWeeklyWorkPayment(sessionId, payload);
      toast.success("Weekly payment updated successfully");
      setHasUnsavedChanges(false);
      navigate(-1);
    } catch (error: any) {
      const response = error?.response?.data;
      const message = Array.isArray(response?.message)
        ? response.message.join(", ")
        : response?.message || "Failed to update weekly payment";
      toast.error(message);
      console.error(`🚀 ~ AddBulkWorkerPayment ~ handleUpdate ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    const nextErrors: any = {};

    if (!isEditMode && !workerRows.length) {
      nextErrors.workers = "Add at least one worker";
    }
    if (!isEditMode && workerRows.length && workerRows.every((r: any) => r.disabled)) {
      nextErrors.workers = "All selected workers already have a payment for this period";
    }
    // 0 is a valid amount to pay (e.g. a worker with no work logged this period whose deductions
    // still need recording), so only block genuinely negative values here. Rows already flagged as
    // having an existing payment are excluded from submission, so their amount doesn't matter.
    const invalidAmounts = workerRows.some((r: any) => !r.disabled && Number(r.amountToPay || 0) < 0);
    if (workerRows.length && invalidAmounts) {
      nextErrors.amount = "Amount to pay cannot be negative";
    }
    const missingProject = workerRows.some((r: any) => !r.disabled && !r.projectId);
    if (workerRows.length && missingProject) {
      nextErrors.project = "Select a project for every worker";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      // Not every error field (e.g. per-row "amount") has a visible helperText slot in the form,
      // so surface a toast too — otherwise a blocked save looks like the button silently does nothing.
      toast.error(Object.values(nextErrors)[0] as string);
      return;
    }
    if (loading) return;

    if (isEditMode) handleUpdate();
    else handleCreate();
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Per-row `disabled` (duplicate-period in create mode, already-paid in edit mode) already locks
  // editing via CrudTable's isCellEditable — no need to blanket-disable every column.
  const tableColumns = useMemo(
    () => getAmountToPayColumns({ projectStagesCache, ensureProjectStages, canViewWelfare }),
    [projectStagesCache, ensureProjectStages, canViewWelfare],
  );

  // Changing the pay period after workers are already in the table would silently wipe their
  // rows (see the payPeriod useEffect above) — once there's at least one row, lock the period
  // and the reference Date field, and explain why via a tooltip.
  const periodLocked = workerRows.length > 0;
  const periodLockedReason = "Remove all workers to change the pay period";

  const viewReportDisabled = !isEditMode || hasUnsavedChanges;
  const viewReportTooltip = !isEditMode ? "Save this payment first" : hasUnsavedChanges ? "Save your changes first" : "";

  return (
    <>
      {(!isEditMode || sessionLoaded) && (
        <CardLayout>
          {allRowsPaid && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This weekly payment has already been paid and can no longer be edited.
            </Alert>
          )}

          {partiallyPaid && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This weekly payment is partially paid. Editing the remaining workers is not recommended — avoid changing projects or
              other details, as it can make the session harder to reconcile against what's already been paid.
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                Pay Period:
              </Typography>
              {!isEditMode && (
                <Tooltip title={periodLocked ? periodLockedReason : ""}>
                  <span>
                    <IconButton size="small" onClick={goToPrevWeek} disabled={periodLocked}>
                      <KeyboardArrowLeft />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, textAlign: "center" }}>
                {payPeriod.display}
              </Typography>
              {!isEditMode && (
                <Tooltip title={periodLocked ? periodLockedReason : ""}>
                  <span>
                    <IconButton size="small" onClick={goToNextWeek} disabled={periodLocked}>
                      <KeyboardArrowRight />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            {!allRowsPaid && (
              <ActionButton buttonText="Add Worker(s)" variant="outlined" onButtonClick={() => setAddWorkerDialogOpen(true)} />
            )}
          </Box>

          {/* Project is per worker-row (see the grid's Project/Stage columns), not session-wide
              (AJCWLT-208) — so a single weekly payment can span multiple projects. */}
          <Box display="flex" gap={2} mb={2}>
            {!isEditMode && <InputField required disabled label="Date" name="date" type="date" value={date} fullWidth />}
            <InputField
              disabled={allRowsPaid}
              label="Description"
              name="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setHasUnsavedChanges(true);
              }}
              fullWidth
              autoComplete="on"
            />
          </Box>

          {errors.workers && (
            <Typography variant="caption" color="error" display="block" mb={1}>
              {errors.workers}
            </Typography>
          )}

          <FormDialog
            open={addWorkerDialogOpen}
            onClose={() => {
              setAddWorkerDialogOpen(false);
              setPendingWorkers([]);
            }}
            onSubmit={handleAddWorkers}
            title="Add Workers"
            actions={
              <>
                <ActionButton
                  buttonText="Cancel"
                  variant="outlined"
                  onButtonClick={() => {
                    setAddWorkerDialogOpen(false);
                    setPendingWorkers([]);
                  }}
                />
                <ActionButton buttonText="Add" loading={fetchingWorkData} disabled={!pendingWorkers.length} type="submit" />
              </>
            }
          >
            <SelectField
              label="Workers"
              value={pendingWorkers}
              getData={HumanResourceService.getEmployees}
              getDataStaticFilters={{
                active: true,
                type: "WORKER",
                sortBy: "name",
                sortOrder: "asc",
              }}
              optionFilter={(options: any[]) => options.filter((o) => !workerRows.some((r) => r.workerId === o.id))}
              multiSelect
              limitTags={3}
              onChange={setPendingWorkers}
              style={{ width: 400 }}
            />
          </FormDialog>

          <CrudTable
            rows={displayRows}
            // CrudTable also calls setRows directly for its own built-in row-delete action
            // (bypassing onRowUpdate entirely), so that path needs its own dirty flag here too —
            // otherwise removing a worker leaves "View Report" wrongly enabled.
            setRows={(updater: any) => {
              setHasUnsavedChanges(true);
              setWorkerRows(updater);
            }}
            columns={tableColumns}
            onRowUpdate={handleRowUpdate}
            hidePagination
            confirmDeleteMessage="Remove this worker from the weekly payment? This only removes them from this unsaved form — save to apply it."
            // In create mode, `disabled` only means "payment already exists for this period" —
            // that row can't be submitted either way, but it should still be removable from the
            // table. In edit mode, `disabled` means "already paid" and stays undeletable (the
            // default behavior below).
            isDeleteDisabled={isEditMode ? undefined : () => false}
          />

          <Box mt={2} p={1.5} display="flex" justifyContent="flex-end" sx={{ background: "#F7F7FC", borderRadius: "8px" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total Amount: ₹ {totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            {canViewReport && (
              <Tooltip title={viewReportTooltip}>
                <span>
                  <ActionButton
                    buttonIcon={Assessment}
                    buttonText="View Report"
                    variant="outlined"
                    disabled={viewReportDisabled}
                    onButtonClick={() => navigate(WEEKLY_WORKER_PAYMENT_ROUTES.report.replace(":sessionId", sessionId as string))}
                  />
                </span>
              </Tooltip>
            )}
            <ActionButton buttonText="Cancel" variant="outlined" onButtonClick={handleCancel} />
            {!allRowsPaid && <ActionButton buttonText="Save" onButtonClick={handleSave} loading={loading || fetchingWorkData} />}
          </Box>
        </CardLayout>
      )}
    </>
  );
};
